/**
 * Valle della Pace variant — UI overrides + extras for tenant `valledellapace`.
 *
 * SCOPE: The old Valle deployment is a Commerce7 full-commerce winery
 * (in-widget cart + login + checkout). This variant ships the browse/contact
 * experience PLUS the Commerce7 in-widget cart and account/login:
 *   - cart (gated on commerce7 + show_cart): sku-keyed CartPanel override +
 *     commerce-aware WineCard with add-to-cart. cart_c7.js owns the BE calls.
 *   - account/login (gated on commerce7 + show_user_account): in-chat LoginForm
 *     + account_c7.js (token in localStorage, greeting, header account button).
 *   - checkout (in the cart gate): DeliveryChoice → Ship/Pickup form →
 *     proceed_checkout → PaymentForm (saved-card / new-card / guest) → charge →
 *     OrderConfirmation. checkout_c7.js owns the BE calls; new-card path sends
 *     RAW card fields → BE → C7 (no client tokenization, confirmed from old FE).
 *
 * CHECKOUT FOLLOW-UPS (documented, non-blocking):
 *   - State/country are text inputs (old used region dropdowns via a
 *     delivery-countries fetch); BE payload fields are identical.
 *   - Field-level C7 errors aren't surfaced yet — core/api.js throws on non-2xx
 *     without the body, so we show a generic message. Needs an api.js change.
 *   - Saved-address selection + edit-in-place forms and the club-signup checkout
 *     are not ported (the old CHECKOUT object's membership/club-cart branches).
 *
 * NO PROMO BANNER: old createBanner (script.js 1583) renders a DATED/expired
 *   promo ("Vertical Library Collection — Valid until February 28, 2025") with
 *   an empty click target. Configure a current promo via banner_* keys instead.
 *
 * Shares Halleck/Hestan's palette (#972553/#574D39/#691B38) and card
 * components (same fork). Contact menu = Email Us + Call Us only (Valle's
 * showContactOptions, like Hestan — no "Text a Live Person").
 *
 * GUIDED FLOW (old feat/valledellapace_c7 script.js 1622-1632):
 *   1. Experience our wines  → "Show me the tasting experience"
 *   2. Shop our wines        → "Show me all available wines"
 *   3. Explore our memberships → "Show me all wine club memberships"
 *   4. Events                → "Show me all the upcoming events"
 *   5. Reviews               → __reviews__
 *   6. Contact our team      → __contact__ (Email Us + Call Us)
 *
 * Reference: feat/valledellapace_c7/script.js 1622-1632 (buttons),
 *   7009-7012 (showContactOptions → Email Us / Call Us).
 */

import { on, addMessage, getState, setState } from "../../core/state.js";
import { getContact, getFeature, getUiFeature, isMembershipEnabled } from "../../core/configAccessors.js";
import { registerMessageKind } from "../../components/base/messages/index.js";
import { registry } from "../../core/registry.js";
import ContactOptions from "../../components/base/messages/ContactOptions.js";
import ValleWineCard from "./components/WineCard.js";
import ValleCartPanel from "./components/CartPanel.js";
import ValleMembershipCard from "./components/MembershipCard.js";
import ValleEventCard from "./components/EventCard.js";
import ValleEventDetailsCard from "./components/EventDetailsCard.js";
import ValleLoginForm from "./components/LoginForm.js";
import ValleCheckoutForm from "./components/CheckoutForm.js";
import ValleOrderReview from "./components/OrderReview.js";
import ValleOrderConfirmation from "./components/OrderConfirmation.js"; 
import * as accountFeature from "../../features/optional/account.js";
import * as cartC7 from "../../features/optional/cart_c7.js";
import * as accountC7 from "../../features/optional/account_c7.js";
import * as checkoutC7 from "../../features/optional/checkout_c7.js";
import * as clubC7 from "../../features/optional/club_c7.js";

function isTrue(v) {
  return v === true || v === "true" || v === "True";
}

// Guided chips are admin-configured (tenant.guided_flow_questions) — no list is
// hard-coded here. The only per-session rule that can't live in static config:
// the tasting chip is hidden for QR entries unless openagent forces it. Apply it
// over the configured list.
function applyQrFilter(chips) {
  const { isQR, openagent } = getState().session || {};
  const showTasting = !isQR || openagent;
  return chips.filter(
    (item) => showTasting || item.value !== "Show me the tasting experience"
  );
}

function postDefaultOptions() {
  addMessage({ role: "bot", kind: "text", data: { text: "Is there anything else I can help you with?" } });
  const items = (getState().tenant || {}).guided_flow_questions || [];
  addMessage({ role: "bot", kind: "prompts", data: { items } });
}

function telHref(phone) {
  return `tel:${String(phone || "").replace(/[^+0-9]/g, "")}`;
}

export async function register({ config, shadow }) {
  registerMessageKind("contact-options", ContactOptions);

  registry.registerComponent("WineCard", ValleWineCard);
  registry.registerComponent("MembershipCard", ValleMembershipCard);
  registry.registerComponent("EventCard", ValleEventCard);
  registry.registerComponent("EventDetailsCard", ValleEventDetailsCard);
  // Reviews now come from the BE (reviews_information, sourced from the tenant
  // REVIEWS_INFORMATION config) — rendered as Valle pills, NOT hardcoded in the
  // variant. Parity with old createReviews, which rendered the BE's review dict.

  // Commerce7 in-widget cart (parity with old createCart/addToCart/showCarts).
  // Gated on commerce7 + show_cart, like Fogarty's Shopify cart. The Valle
  // CartPanel is sku-keyed (overrides the base variantId-keyed one), and the
  // WineCard self-detects commerce mode to show its add-to-cart control.
  if (isTrue(getFeature("commerce7")) && getUiFeature("show_cart", false)) {
    cartC7.init();
    checkoutC7.init({ flow: "reviewed" });
    // Checkout: proceed_checkout (Ship/Pickup) → charge (saved/new/guest) →
    // confirmation. "Proceed to checkout" in the cart panel emits ws-checkout.
    registerMessageKind("checkout-form", ValleCheckoutForm);
    registerMessageKind("order-review", ValleOrderReview);
    registerMessageKind("order-confirmation", ValleOrderConfirmation);
    // Re-prompt with the guided buttons once the order is placed (parity with
    // the contact-flow follow-ups below).
    on("ws-order-confirmed", () => {
      postDefaultOptions();
    });
  }

  // Commerce7 in-widget account/login (parity with old showLoginForm + onLogin).
  // Gated on commerce7 + show_user_account. Shows the login form on first open
  // when logged out, greets returning customers, and owns the customer token.
  if (isTrue(getFeature("commerce7")) && getUiFeature("show_user_account", false)) {
    registerMessageKind("login-form", ValleLoginForm);
    accountFeature.init();
  }

  // Commerce7 wine-club membership — common feature (features/optional/
  // club_c7.js works for any Commerce7 tenant); currently only turned on for
  // tenants in configAccessors.js's isMembershipEnabled() allowlist. Message
  // kinds are already registered unconditionally in components/base/messages/
  // index.js — this just starts the orchestrator. MembershipCard's Join
  // button self-gates on the same check, so nothing else needs to change here
  // if this is ever turned off again.
  if (isMembershipEnabled()) {
    clubC7.init();
  }

  const cfg = getState().tenant || {};
  const chips = Array.isArray(cfg.guided_flow_questions) ? cfg.guided_flow_questions : [];
  setState({ tenant: { ...cfg, guided_flow_questions: applyQrFilter(chips) } });

  // NOTE: the welcome hero banner is config-driven — set `banner_image_url` in
  // the admin Tenant Configuration (Banner.js renders it). The deliverable
  // tenant-config.json ships that key (valle-banner.webp). No code fallback.

  on("ws-prompt-select", ({ value }) => {
    if (value === "__contact__") {
      addMessage({ role: "bot", kind: "text", data: { text: "How would you like to connect with our team?" } });
      // Valle menu = Email Us + Call Us only (no Text a Live Person).
      addMessage({
        role: "bot",
        kind: "contact-options",
        data: { email: getContact("email"), phone: getContact("phone"), text_live_person: false },
      });
      return;
    }
  });

  // Exact old copy (script.js showContactNumber 7017-7022, showContactEmailMessage
  // 7024-7029) — the specific Valle business hours, not a generic "business hours".
  // Winery-specific copy; lives in this variant (Valle only).
  on("ws-contact-call", () => {
    const phone = getContact("phone");
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: `You can call us at [${phone}](${telHref(phone)}). Our team is available between 10 a.m. to 6 p.m. on Wednesday & Thursday and 11 a.m. to 7 p.m. on Friday & Saturday.` },
    });
    postDefaultOptions();
  });

  on("ws-contact-email", () => {
    const phone = getContact("phone");
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: `We'll get back to you within 24–48 hours. For faster assistance, please call us at [${phone}](${telHref(phone)}) between 10 a.m. to 6 p.m. on Wednesday & Thursday and 11 a.m. to 7 p.m. on Friday & Saturday.` },
    });
    postDefaultOptions();
  });
}
